'use client';

import { createContext, type JSX, type PropsWithChildren, useContext } from 'react';

/**
 * AI-agnostic bridge between the shared voiceover UI and the AI Toolkit.
 *
 * The voiceover feature is rendered on two routes: the legacy `/voiceover` page (no AI Toolkit
 * context) and the `/ai/voiceover` page (wrapped by AiVoiceoverWrapper, which lives inside the
 * AiToolkitProvider). To keep the shared components free of AI Toolkit coupling, they consume this
 * generic bridge instead. On the legacy route there is no provider, so the inert default below makes
 * every consumer a no-op; on the AI route, AiVoiceoverWrapper fills it with AI Toolkit state.
 */
export interface VoiceoverGenerationBridgeValue {
    /** When true, the shared GenerateButton is disabled (an AI Toolkit task is in progress). */
    submitDisabled: boolean;
}

const DEFAULT_BRIDGE: VoiceoverGenerationBridgeValue = {
    submitDisabled: false,
};

const VoiceoverGenerationBridgeContext = createContext<VoiceoverGenerationBridgeValue>(DEFAULT_BRIDGE);

export function VoiceoverGenerationBridgeProvider({
    value,
    children,
}: PropsWithChildren<{ value: VoiceoverGenerationBridgeValue }>): JSX.Element {
    return (
        <VoiceoverGenerationBridgeContext.Provider value={value}>{children}</VoiceoverGenerationBridgeContext.Provider>
    );
}

export function useVoiceoverGenerationBridge(): VoiceoverGenerationBridgeValue {
    return useContext(VoiceoverGenerationBridgeContext);
}
